import { useEffect, useState } from "react";
import { Form, Modal } from "react-bootstrap";
import Button from "react-bootstrap/Button";
import { BiSolidError } from "react-icons/bi";
import {
  FaPlus,
  FaHourglassHalf,
  FaSearch,
  FaBan,
  FaMoneyBillWave,
} from "react-icons/fa";
import { IoCloseSharp } from "react-icons/io5";
import "react-loading-skeleton/dist/skeleton.css";
import { useTranslation } from "react-i18next";
import { RiDeleteBin6Fill } from "react-icons/ri";
import { MdDeleteForever } from "react-icons/md";
import { NavLink } from "react-router-dom";
import { toast } from "react-toastify";
import { useDebounce } from "use-debounce";
import { useSelector } from "react-redux";
import CancelIcon from "../../../assets/images/cancel-icon.svg";
import YesIcon from "../../../assets/images/Yes-Icon.svg";
import AdminPagination from "../../../component/pagination/AdminPagination";
import SkeletonLoading from "../../../component/SkeletonLoading";
import i18n from "../../../store/api/i18n";
import {
  useActivateMultiUserMutation,
  useDeleteMultiUserMutation,
  useDeleteSingleUserMutation,
  usePermanentDeleteUserMutation,
  useBulkPermanentDeleteUserMutation,
  useGetAllUsersQuery,
} from "../../../store/api/userSlice";
import { getErrorMessage } from "../../../utils";
import "./vendors-schools.scss";
import { capitalizeString } from "../../../utils/dateAndTimeConversion";

const initialFilters = {
  role: "",
  email: "",
  status: "",
};

const initialPagination = {
  page: 1,
  limit: 10,
};

const StatusIcon = ({ status }) => {
  switch (status) {
    case "active":
      return <img src={YesIcon} alt="Active" className="status-icon" />;
    case "pending":
      return <FaHourglassHalf color="#ffc107" title="Pending" />;
    case "inreview":
      return <FaSearch color="#0d6efd" title="In Review" />;
    case "suspended":
      return <FaBan color="#dc3545" title="Suspended" />;
    case "payment_pending":
      return <FaMoneyBillWave color="#fd7e14" title="Payment Pending" />;
    case "inactive":
    default:
      return <img src={CancelIcon} alt="Inactive" className="status-icon" />;
  }
};

const VendorSchoolsPage = () => {
  const { t } = useTranslation();
  const [show, setShow] = useState(false);
  const [action, setAction] = useState("");
  const [deleteTargetEmail, setDeleteTargetEmail] = useState("");
  const [deleteReason, setDeleteReason] = useState("");
  const [confirmEmailInput, setConfirmEmailInput] = useState("");

  const currentUser = useSelector((state) => state.UserReducer?.user);
  const isSuperAdmin = currentUser?.role === "super_admin";

  const [users, setusers] = useState([]);
  const [search, setSearch] = useState("");
  const [debouncedSearch] = useDebounce(search, 300);
  // state for single and multi user store id
  const [selectedUser, setSelectedUser] = useState({
    id: "",
    ids: [],
  });

  const [pagination, setPagination] = useState(initialPagination);

  const [filters, setFilters] = useState(initialFilters);

  const { data, isSuccess, isLoading, isError, error, isFetching, refetch } =
    useGetAllUsersQuery(
      {
        page: pagination.page,
        limit: pagination.limit,
        ...filters,
      },
      { refetchOnMountOrArgChange: true },
    );

  // delete single user
  const [deleteSingleUser, { isLoading: isDeleteLoading }] =
    useDeleteSingleUserMutation();
  const [deleteMultiUser, { isLoading: isMultiDeleteLoading }] =
    useDeleteMultiUserMutation();
  const [permanentDeleteUser, { isLoading: isPermanentDeleteLoading }] =
    usePermanentDeleteUserMutation();
  const [bulkPermanentDeleteUser, { isLoading: isBulkPermanentDeleteLoading }] =
    useBulkPermanentDeleteUserMutation();

  const [activateMultiUser, { isLoading: isMultiActivateLoading }] =
    useActivateMultiUserMutation();

  if (isError) {
    toast.dismiss();
    toast.error(getErrorMessage(error));
  }

  const handlePageChange = (page) => {
    setPagination((prev) => ({ ...prev, page }));
  };

  const handleLimitChange = (limit) => {
    setPagination((prev) => ({
      ...prev,
      limit,
      page: 1,
    }));
  };

  useEffect(() => {
    if (isSuccess && data?.data) {
      setusers(data.data.users);
      setPagination(data?.data?.pagination);
    }
  }, [data, isSuccess]);

  const handleClose = () => {
    setShow(false);
    setDeleteReason("");
    setConfirmEmailInput("");
  };
  const handleModel = () => {
    if (action === "deleteMulti" || action === "permanentDeleteMulti") {
      if (selectedUser.ids.length === 0) {
        toast.error(t("Please select at least one user"));
        return;
      }
      setShow(true);
    } else {
      handleUserAction();
    }
  };

  // for check user
  const handleCheckUser = (id) => {
    setSelectedUser((prev) => ({
      ...prev,
      ids: prev.ids.includes(id)
        ? prev.ids.filter((userId) => userId !== id) // remove
        : [...prev.ids, id], // add
    }));
  };

  // handle check all user — super_admin rows are never selectable
  const handleCheckAll = () => {
    const selectableIds = users
      .filter((curElem) => curElem.role !== "super_admin")
      .map((curElem) => curElem.id);
    setSelectedUser((prev) => ({
      ...prev,
      ids: prev.ids.length === selectableIds.length ? [] : selectableIds,
    }));
  };

  const handleUserAction = async () => {
    toast.dismiss();
    if (!action) return;

    if (!selectedUser.id && selectedUser.ids.length === 0) {
      // toast.error(t("Please select at least one user"));
      toast.error(t("select_at_least_one_user"));
      return;
    }

    if (
      action === "permanentDelete" &&
      confirmEmailInput.trim() !== deleteTargetEmail
    ) {
      toast.error(t("permanent_delete_email_mismatch"));
      return;
    }

    if (
      action === "permanentDeleteMulti" &&
      confirmEmailInput.trim() !== "DELETE"
    ) {
      toast.error(t("permanent_delete_phrase_mismatch"));
      return;
    }

    try {
      let response;
      action === "singleDelete"
        ? (response = await deleteSingleUser({
            id: selectedUser.id,
            reason: deleteReason,
          }).unwrap())
        : action === "permanentDelete"
          ? (response = await permanentDeleteUser({
              id: selectedUser.id,
              reason: deleteReason,
            }).unwrap())
          : action === "permanentDeleteMulti"
            ? (response = await bulkPermanentDeleteUser({
                userIds: selectedUser.ids,
                reason: deleteReason,
              }).unwrap())
            : action === "deleteMulti"
              ? (response = await deleteMultiUser({
                  userIds: selectedUser.ids,
                }).unwrap())
              : (response = await activateMultiUser({
                  userIds: selectedUser.ids,
                  active: action === "activeMlti",
                }).unwrap());

      if (response.statusCode === 201 || response.statusCode === 200) {
        if (action === "permanentDeleteMulti") {
          const { succeeded, failed } = response.data || {};
          if (failed?.length) {
            toast.warn(
              `${succeeded?.length || 0} deleted, ${failed.length} failed.`,
            );
          } else {
            toast.success(response.message);
          }
        } else {
          toast.success(response.message);
        }
        setShow(false);
        setSelectedUser({ id: "", ids: [] });
        setAction("");
        setDeleteReason("");
        setConfirmEmailInput("");
        refetch();
      }
    } catch (err) {
      const mesg = err.data?.message || "Something went wrong";
      toast.error(mesg);
    }
  };

  const handleResetFilters = () => {
    setPagination((prev) => ({ ...prev, ...initialPagination }));
    setFilters(initialFilters);
    setAction("");
    setSearch("");
  };

  // search State & Debounce Setup
  useEffect(() => {
    if (debouncedSearch) {
      setPagination((prev) => ({ limit: 10, page: 1 }));
      setFilters((prev) => ({ ...prev, email: search }));
    }
  }, [debouncedSearch]);

  useEffect(() => {
    if (!search.trim()) {
      setFilters((prev) => ({ ...prev, email: "" }));
    }
  }, [search]);

  // Language Change Handler
  useEffect(() => {
    const h = () => {
      refetch?.();
    };
    i18n.on("languageChanged", h);
    return () => i18n.off("languageChanged", h);
  }, [refetch]);

  return (
    <>
      <div className="d-flex justify-content-between align-items-center gap-2 flex-wrap w-100 mb-2">
        <div className="total">
          {t("total")} {t("users")}: {pagination?.total}
        </div>

        <div className="d-flex justify-content-between align-items-center gap-2">
          <select
            className="form-select"
            value={action}
            onChange={(e) => setAction(e.target.value)}
          >
            <option value="">
              {t("select")} {t("bulk")} {t("actions")}
            </option>
            <option value="activeMlti">
              {t("activate")} {t("selected")} {t("users")}
            </option>
            <option value="inactiveMlti">
              {t("inactivate")} {t("selected")} {t("users")}
            </option>
            {isSuperAdmin && (
              <option value="permanentDeleteMulti">
                {t("permanently_delete")} {t("selected")} {t("users")}
              </option>
            )}
          </select>
          <button
            className="btn btn-primary me-2"
            onClick={handleModel}
            disabled={isMultiActivateLoading || isBulkPermanentDeleteLoading}
          >
            {isMultiActivateLoading || isBulkPermanentDeleteLoading ? (
              <div className="spinner-border loaderStyle2" role="status">
                <span className="sr-only p-0"></span>
              </div>
            ) : (
              t("go")
            )}
          </button>
          <span className="selected-info">
            {selectedUser.ids.length} of {users.length} {t("selected")}
          </span>
        </div>
      </div>

      <div className="d-flex justify-content-between align-items-center gap-2 flex-wrap w-100 mb-2">
        <div className="d-flex flex-wrap gap-2 ms-auto">
          <div className="d-flex flex-grow-1">
            <input
              type="text"
              className="form-control custom-field-one"
              placeholder={`${t("search")}..`}
              value={search}
              onChange={(e) => setSearch(e.target.value.trim())}
            />
          </div>
          <div className="d-flex flex-grow-1">
            <select
              className="form-select"
              value={filters.role}
              onChange={(e) =>
                setFilters((prev) => ({ ...prev, role: e.target.value }))
              }
            >
              <option value="">
                {t("select")} {t("user")} {t("role")}
              </option>
              <option value="school">{t("userRole.school")}</option>
              <option value="vendor">{t("userRole.vendor")}</option>
              <option value="driver">{t("userRole.driver")}</option>
              <option value="parent">{t("userRole.parent")}</option>
            </select>
          </div>
          <div className="d-flex flex-grow-1">
            <select
              className="form-select"
              value={filters.status}
              onChange={(e) =>
                setFilters((prev) => ({ ...prev, status: e.target.value }))
              }
            >
              <option value="">
                {t("select")} {t("user")} {t("status")}
              </option>
              <option value="active">{t("active")}</option>
              <option value="inactive">{t("inactive")}</option>
              <option value="pending">{t("pending")}</option>
              <option value="inreview">{t("status_inreview")}</option>
              {/* <option value="suspended">{t("status_suspended")}</option> */}
              <option value="payment_pending">
                {t("status_payment_pending")}
              </option>
            </select>
          </div>
          <div className="d-flex gap-2 ms-auto">
            <button
              className="btn btn-primary addDriver "
              onClick={handleResetFilters}
            >
              {t("btns.reset")}
            </button>

            <NavLink
              to="add"
              className="page-add-btn d-flex align-items-center gap-2"
            >
              <FaPlus />{" "}
              {`${t("add")} ${t("vendors")}/${t("school")} ${t("owner")}`}
            </NavLink>
          </div>
        </div>
      </div>

      <div className="adminVendorSchools">
        <div className="mb-2 filterBox justify-content-start align-items-start">
          <li></li>
        </div>

        <div className="table m-0">
          <table>
            <thead>
              <tr>
                <th>
                  <Form.Check
                    type="checkbox"
                    checked={
                      users.some((curElem) => curElem.role !== "super_admin") &&
                      selectedUser.ids.length ===
                        users.filter((curElem) => curElem.role !== "super_admin")
                          .length
                    }
                    onChange={handleCheckAll}
                  />
                </th>
                <th>
                  {t("email")} {t("address")}
                </th>
                <th>
                  {t("user")} {t("type")}
                </th>
                <th>{t("activate")}</th>
                <th>
                  {t("contact")} {t("number")}
                </th>
                <th>{t("name")}</th>
                {isSuperAdmin && <th>{t("btns.delete")}</th>}
              </tr>
            </thead>
            <tbody>
              {isLoading || isFetching ? (
                <SkeletonLoading length={7} />
              ) : users.length > 0 ? (
                users.map((curElem, i) => (
                  <tr key={i}>
                    <td>
                      {curElem?.role !== "super_admin" && (
                        <Form.Check
                          type="checkbox"
                          checked={selectedUser.ids.includes(curElem.id)}
                          onChange={() => handleCheckUser(curElem.id)}
                        />
                      )}
                    </td>
                    <td>
                      {/* <Link
                            to={`/user-management/edit/${curElem.id}`}
                            className="ps-4 fw-bold"
                            style={{ color: "#007bff" }}
                          > */}
                      {curElem.email ? curElem.email : "N/A"}
                      {/* </Link> */}
                    </td>
                    <td className="text-nowrap">
                      {curElem.role ? capitalizeString(curElem.role) : "N/A"}
                    </td>
                    <td className="d-flex align-items-center gap-2">
                      <StatusIcon status={curElem?.status} />
                      <span style={{ fontSize: "0.8rem" }}>
                        {t(
                          curElem?.status === "inreview"
                            ? "status_inreview"
                            : curElem?.status === "suspended"
                              ? "status_suspended"
                              : curElem?.status === "payment_pending"
                                ? "status_payment_pending"
                                : curElem?.status || "",
                        )}
                      </span>
                    </td>
                    <td>{curElem.phone ? curElem.phone : "N/A"}</td>
                    <td>{curElem.full_name ? curElem.full_name : "N/A"}</td>
                    {isSuperAdmin && (
                      <td className="d-flex gap-2">
                        {curElem?.role !== "super_admin" && (
                          <>
                            <Button
                              className="btn-danger p-2"
                              title={`${t("btns.delete")} (${t("soft_delete")})`}
                              onClick={() => {
                                setShow(true);
                                setAction("singleDelete");
                                setDeleteTargetEmail(
                                  curElem.email ||
                                    curElem.full_name ||
                                    "this user",
                                );
                                setSelectedUser((prev) => ({
                                  ...prev,
                                  id: curElem.id,
                                }));
                              }}
                            >
                              <RiDeleteBin6Fill />
                            </Button>
                            {/* <Button
                              className="btn-dark p-2"
                              title={t("permanently_delete")}
                              onClick={() => {
                                setShow(true);
                                setAction("permanentDelete");
                                setDeleteTargetEmail(curElem.email || "");
                                setSelectedUser((prev) => ({
                                  ...prev,
                                  id: curElem.id,
                                }));
                              }}
                            >
                              <MdDeleteForever />
                            </Button> */}
                          </>
                        )}
                      </td>
                    )}
                  </tr>
                ))
              ) : (
                <tr className="text-center">
                  <td colSpan="12">
                    <div>
                      <span>
                        {" "}
                        {t("no")} {t("users")} {t("found")}
                      </span>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      <AdminPagination
        totalPages={pagination.pages}
        currentPage={pagination.page}
        limit={pagination.limit}
        onPageChange={handlePageChange}
        onLimitChange={handleLimitChange}
      />

      <Modal show={show} centered>
        <Button className="closePopup" onClick={() => handleClose("show")}>
          <IoCloseSharp />
        </Button>
        <div className="popupBox p-3 p-md-5">
          <div>
            <div className="text-center mb-3 errorIcon">
              <BiSolidError />
            </div>
            <div className="text-center mb-2 deleteHd">
              {action === "permanentDelete" || action === "permanentDeleteMulti"
                ? t("confirmPermanentDelete")
                : t("confirmDelete")}
            </div>
            {(action === "singleDelete" || action === "permanentDelete") &&
              deleteTargetEmail && (
                <div
                  className="text-center mb-2"
                  style={{ fontSize: "0.9rem" }}
                >
                  <strong>{deleteTargetEmail}</strong>
                </div>
              )}
            {(action === "deleteMulti" || action === "permanentDeleteMulti") &&
              selectedUser.ids.length > 0 && (
                <div
                  className="text-center mb-2"
                  style={{ fontSize: "0.9rem" }}
                >
                  {selectedUser.ids.length} {t("users")}
                </div>
              )}
            {action === "permanentDelete" ||
            action === "permanentDeleteMulti" ? (
              <div
                className="mb-3 p-2 text-center"
                style={{
                  background: "#f8d7da",
                  border: "1px solid #dc3545",
                  borderRadius: "8px",
                  fontSize: "0.82rem",
                  color: "#58151c",
                }}
              >
                ⚠️ {t("permanent_delete_warning")}
              </div>
            ) : (
              <div
                className="mb-3 p-2 text-center"
                style={{
                  background: "#fff3cd",
                  border: "1px solid #ffc107",
                  borderRadius: "8px",
                  fontSize: "0.82rem",
                  color: "#856404",
                }}
              >
                ⚠️ This action <strong>cannot be undone</strong>. The user will
                be permanently removed and will no longer be able to log in.
              </div>
            )}
            {action === "permanentDelete" && (
              <div className="mb-3">
                <label className="form-label" style={{ fontSize: "0.85rem" }}>
                  {t("permanent_delete_type_email", {
                    email: deleteTargetEmail,
                  })}
                </label>
                <input
                  type="text"
                  className="form-control mb-2"
                  value={confirmEmailInput}
                  onChange={(e) => setConfirmEmailInput(e.target.value)}
                  placeholder={deleteTargetEmail}
                  style={{ fontSize: "0.9rem" }}
                />
              </div>
            )}
            {action === "permanentDeleteMulti" && (
              <div className="mb-3">
                <label className="form-label" style={{ fontSize: "0.85rem" }}>
                  {t("permanent_delete_type_phrase")}
                </label>
                <input
                  type="text"
                  className="form-control mb-2"
                  value={confirmEmailInput}
                  onChange={(e) => setConfirmEmailInput(e.target.value)}
                  placeholder="DELETE"
                  style={{ fontSize: "0.9rem" }}
                />
              </div>
            )}
            {(action === "singleDelete" ||
              action === "permanentDelete" ||
              action === "permanentDeleteMulti") && (
              <div className="mb-3">
                <textarea
                  className="form-control"
                  rows={3}
                  placeholder={t("deletion_reason_placeholder")}
                  value={deleteReason}
                  onChange={(e) => setDeleteReason(e.target.value)}
                  style={{ fontSize: "0.9rem", resize: "none" }}
                />
              </div>
            )}
            <div className="text-center">
              <Button
                className="smallBtn cancel me-3"
                onClick={() => handleClose("show")}
                disabled={
                  isMultiDeleteLoading ||
                  isDeleteLoading ||
                  isPermanentDeleteLoading ||
                  isBulkPermanentDeleteLoading
                }
              >
                {t("btns.cancel")}
              </Button>
              <Button
                className="smallBtn auto"
                onClick={handleUserAction}
                disabled={
                  isMultiDeleteLoading ||
                  isDeleteLoading ||
                  isPermanentDeleteLoading ||
                  isBulkPermanentDeleteLoading ||
                  (action === "permanentDelete" &&
                    confirmEmailInput.trim() !== deleteTargetEmail) ||
                  (action === "permanentDeleteMulti" &&
                    confirmEmailInput.trim() !== "DELETE")
                }
              >
                {isMultiDeleteLoading ||
                isDeleteLoading ||
                isPermanentDeleteLoading ||
                isBulkPermanentDeleteLoading ? (
                  <div className="spinner-border" role="status">
                    <span className="sr-only p-0"></span>
                  </div>
                ) : (
                  t("btns.delete")
                )}
              </Button>
            </div>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default VendorSchoolsPage;
